/**
 * <cart-free-gift>
 *
 * Multi-tier free gift progress banner + gift selector for the cart drawer.
 *
 * Reads tier config (thresholds, labels, gift products) from the JSON script
 * tag rendered by snippets/cart-free-gift.liquid. On every cart change,
 * re-fetches /cart.js and re-renders:
 *   - A progress bar with milestone dots at each tier threshold
 *   - Progress text lines for locked tiers
 *   - A gift selector (single or multiple mode) for unlocked+ungifted tiers
 *
 * Gift line items are tagged with properties._free_gift_tier = <tierId> so they
 * can be identified later. Cart line items detect this tag via Liquid and show
 * the "Added for free" badge + "Change gift" / "Remove" actions.
 *
 * NOTE: Making the gift free at checkout requires a Shopify automatic discount
 * (100% off) or Shopify Function targeting items with the _free_gift_tier
 * property. This component handles UI only.
 *
 * Single-variant products (whether Shopify calls the variant "Default Title"
 * or a real name like "Chocolate / One size") always render their size
 * <select> pre-selected on the only available variant, with the "Add for
 * free" / "Add to cart" button enabled immediately - no extra click needed.
 */

(function () {
  'use strict';

  if (customElements.get('cart-free-gift')) return;

  const GIFT_PROPERTY_KEY = '_free_gift_tier';

  class CartFreeGift extends HTMLElement {
    constructor() {
      super();
      this.banner = null;
      this.selector = null;
      this.config = null;
      this.subtotal = 0;
      this.items = [];
      this.selections = {}; // { [tierId]: { [productId]: variantId } }
      this.openAccordions = {}; // { [tierId]: boolean }

      this.onCartChange = this.onCartChange.bind(this);
      this._onChangeGiftClick = this._onChangeGiftClick.bind(this);
    }

    connectedCallback() {
      const configScript = this.querySelector('script[data-free-gift-config]');
      if (!configScript) return;

      try {
        this.config = JSON.parse(configScript.textContent);
      } catch (e) {
        console.error('cart-free-gift: invalid config JSON', e);
        return;
      }

      this.banner = this.querySelector('[data-free-gift-banner]');
      this.selector = this.querySelector('[data-free-gift-selector]');

      this.subtotal = this.config.initialSubtotal || 0;
      this.items = this.config.initialItems || [];

      this.config.tiers.forEach((tier) => {
        this.selections[tier.id] = {};
        if (tier.products && tier.products.length > 1) {
          this.openAccordions[tier.id] = false;
        }
      });

      this.render();

      document.addEventListener('theme:cart:change', this.onCartChange);
      document.addEventListener('theme:product:added', this.onCartChange);
      document.addEventListener('click', this._onChangeGiftClick);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.onCartChange);
      document.removeEventListener('theme:product:added', this.onCartChange);
      document.removeEventListener('click', this._onChangeGiftClick);
    }

    giftIconSvg() {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.8301 0.730652C11.4277 0.778202 11.9471 1.12747 12.2207 1.66034L12.2217 1.66132C12.4585 2.13022 12.4709 2.66615 12.2627 3.1369L12.1602 3.33417C11.8502 3.84521 11.3075 4.15573 10.707 4.16132H10.5732C10.5354 4.17643 10.495 4.18475 10.4551 4.18475H8.29688V6.43182H13.5518C13.6573 6.4318 13.7441 6.34495 13.7441 6.23944V4.37714C13.7441 4.2717 13.6572 4.18477 13.5518 4.18475H12.6377C12.4495 4.18458 12.2959 4.03121 12.2959 3.84296C12.2959 3.6547 12.4495 3.50134 12.6377 3.50116H13.5518C14.0302 3.50555 14.4198 3.89767 14.4199 4.37714V6.23944C14.4199 6.72158 14.026 7.11623 13.5439 7.11639H12.9717V14.3986C12.9716 14.8808 12.5779 15.2746 12.0957 15.2746H3.7998C3.31781 15.2744 2.92389 14.8807 2.92383 14.3986V8.00018C2.92383 7.81192 3.07739 7.65757 3.26562 7.65741C3.45399 7.65741 3.6084 7.81182 3.6084 8.00018V14.3986C3.60846 14.5039 3.69452 14.5908 3.7998 14.591H12.0957C12.2012 14.591 12.288 14.5041 12.2881 14.3986V7.11639H2.35254C1.87031 7.11639 1.47559 6.72167 1.47559 6.23944V4.37714C1.47566 3.89497 1.87035 3.50116 2.35254 3.50116H3.86133C3.81907 3.44805 3.77897 3.39195 3.74219 3.33221L3.74121 3.33124C3.43133 2.82009 3.40899 2.19364 3.68262 1.66034C3.95622 1.12729 4.47645 0.778135 5.07422 0.730652C5.67134 0.683281 6.24183 0.939807 6.59668 1.42303L7.95215 3.26385L9.30762 1.42303C9.66252 0.939888 10.233 0.683222 10.8301 0.730652ZM2.35742 4.18475C2.25202 4.18487 2.16508 4.27172 2.16504 4.37714V6.23944C2.16507 6.34487 2.25202 6.43171 2.35742 6.43182H7.60938V4.18475H2.35742ZM5.17676 1.41034C5.16172 1.41099 5.14454 1.41229 5.12793 1.41229C4.77166 1.44413 4.46038 1.65241 4.29688 1.97186L4.27441 2.01678C4.13602 2.32782 4.15421 2.68169 4.33203 2.97577C4.51901 3.2849 4.84493 3.47133 5.20605 3.47382L7.27246 3.49335L6.04785 1.83026C5.85079 1.56347 5.54725 1.40936 5.21875 1.40936C5.20603 1.40937 5.19237 1.40966 5.17676 1.41034ZM10.6904 1.40936C10.3645 1.40936 10.0584 1.56352 9.86133 1.83026L9.83789 1.86151L9.83496 1.86053L8.63184 3.49335L10.7002 3.47675L10.834 3.46698C11.1412 3.42412 11.4108 3.24872 11.5742 2.9787C11.7613 2.66943 11.7767 2.29313 11.6123 1.97186C11.4479 1.65064 11.1341 1.44135 10.7754 1.41132H10.7744C10.7491 1.40901 10.7205 1.40936 10.6904 1.40936Z" fill="currentColor" stroke="currentColor" stroke-width="0.15"></path></svg>`;
    }

    onCartChange() {
      fetch(`${window.theme?.routes?.cart_url || '/cart'}.js`)
        .then((res) => res.json())
        .then((cart) => {
          let giftTotal = 0;
          this.items = cart.items.map((item) => {
            const props = item.properties || {};
            if (props[GIFT_PROPERTY_KEY]) {
              const price = item.final_line_price !== undefined ? item.final_line_price : (item.final_price * item.quantity);
              giftTotal += price;
            }
            return {
              key: item.key,
              variant_id: item.variant_id,
              properties: props,
            };
          });
          this.subtotal = cart.total_price - giftTotal;

          // Auto-remove any gift items that no longer qualify due to subtotal dropping below the threshold
          let giftRemoved = false;
          const updates = {};

          this.config.tiers.forEach((tier) => {
            const hasGift = this.tierHasGiftInCart(tier.id);
            const isUnlocked = this.subtotal >= tier.threshold;

            if (hasGift && !isUnlocked) {
              const lines = this.items.filter(
                (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === tier.id
              );
              lines.forEach((line) => {
                updates[line.key] = 0;
              });
              giftRemoved = true;
            }
          });

          if (giftRemoved) {
            // Guard shared across instances (cart drawer + product page render
            // their own <cart-free-gift>) so the removal request fires only once.
            if (window.__cartFreeGiftRemoving) return;
            window.__cartFreeGiftRemoving = true;

            fetch(`${window.theme?.routes?.cart_update_url || '/cart/update'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            })
              .then(() => {
                document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
                this.onCartChange();
              })
              .catch((err) => console.error('cart-free-gift: failed to auto-remove unqualified gifts', err))
              .finally(() => {
                window.__cartFreeGiftRemoving = false;
              });
            return;
          }

          this.render();
        })
        .catch((err) => console.error('cart-free-gift: failed to refresh cart', err));
    }

    /** Handle "Change gift" button clicks anywhere in the cart (event delegation). */
    _onChangeGiftClick(event) {
      const btn = event.target.closest('[data-change-gift]');
      if (!btn) return;
      event.preventDefault();
      const tierId = btn.dataset.changeGift;
      if (tierId) this.removeGiftForTier(tierId);
    }

    tierHasGiftInCart(tierId) {
      return this.items.some(
        (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === tierId
      );
    }

    getTierState(tier) {
      return {
        unlocked: this.subtotal >= tier.threshold,
        giftAdded: this.tierHasGiftInCart(tier.id),
      };
    }

    formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
    }

    renderProgressBar() {
      const tiers = this.config.tiers;
      const maxThreshold = Math.max(...tiers.map((t) => t.threshold));
      const fillPercent = Math.min(100, (this.subtotal / maxThreshold) * 100);

      // Starting dot (always shown/reached as long as component is visible)
      const startDot = `<span class="cart-free-gift__milestone cart-free-gift__milestone--start is-reached">${this.giftIconSvg()}</span>`;

      const milestones = tiers
        .map((tier) => {
          const pos = (tier.threshold / maxThreshold) * 100;
          const reached = this.subtotal >= tier.threshold;
          return `<span class="cart-free-gift__milestone${reached ? ' is-reached' : ''}" style="left:${pos}%">${this.giftIconSvg()}</span>`;
        })
        .join('');

      return `<div class="cart-free-gift__progress">
  <div class="cart-free-gift__progress-track">
    <div class="cart-free-gift__progress-fill" style="width:${fillPercent}%"></div>
    ${startDot}${milestones}
  </div>
</div>`;
    }

    render() {
      if (!this.config) return;

      // PDP mode: keep the whole widget hidden while every tier is still locked
      // (the product page never shows the progress bar / "add more" banner).
      if (this.config.hideWhenLocked) {
        const allLocked = this.config.tiers.every((tier) => this.subtotal < tier.threshold);
        if (allLocked) {
          this.hidden = true;
          return;
        }
      }

      const bannerLines = [];
      const selectorBlocks = [];

      this.config.tiers.forEach((tier, index) => {
        const state = this.getTierState(tier);

        if (state.giftAdded) return;

        // If a higher tier is unlocked and still ungifted, show only that tier's
        // selector - the shopper picks the best gift they qualify for first.
        for (let i = index + 1; i < this.config.tiers.length; i++) {
          const higherState = this.getTierState(this.config.tiers[i]);
          if (higherState.unlocked && !higherState.giftAdded) return;
        }

        if (!state.unlocked) {
          const remaining = tier.threshold - this.subtotal;
          const isPrimary = bannerLines.length === 0;
          const template = isPrimary ? this.config.strings.addMorePrimary : this.config.strings.addMoreSecondary;

          const text = template
            .replace('__AMOUNT__', this.formatMoney(remaining))
            .replace('__LABEL__', tier.label);

          const lineClass = isPrimary ? 'cart-free-gift__line cart-free-gift__line--primary' : 'cart-free-gift__line cart-free-gift__line--secondary';
          bannerLines.push(`<p class="${lineClass}">${text}</p>`);
          return;
        }

        selectorBlocks.push(this.renderTierSelector(tier));
      });

      this.maybeAutoAddGifts();

      const allResolved = bannerLines.length === 0 && selectorBlocks.length === 0;
      const showBanner = bannerLines.length > 0 && selectorBlocks.length === 0;

      if (this.banner) {
        this.banner.innerHTML = bannerLines.join('') + this.renderProgressBar();
        this.banner.hidden = !showBanner;
      }

      if (this.selector) {
        this.selector.innerHTML = selectorBlocks.join('');
        this.selector.hidden = selectorBlocks.length === 0;
        this.bindSelectorEvents();
      }

      this.classList.toggle('cart-free-gift--resolved', allResolved);
      this.hidden = allResolved;
    }

    /**
     * Tiers whose only gift is a product WITHOUT variants are added to the cart
     * automatically the moment they unlock - the shopper has nothing to choose.
     * A sessionStorage marker prevents re-adding after a manual removal, and is
     * cleared again when the tier locks, so a fresh unlock auto-adds again.
     * Guards are window-level because the cart drawer and the product page each
     * render their own <cart-free-gift> instance.
     */
    maybeAutoAddGifts() {
      this.config.tiers.forEach((tier) => {
        if (!tier.products || tier.products.length !== 1) return;

        const product = tier.products[0];
        const variant = product.variants && product.variants[0];
        if (!product.hasOnlyDefaultVariant || !variant || !variant.available) return;

        const state = this.getTierState(tier);
        if (state.giftAdded) return;

        const storageKey = `freeGiftAutoAdded:${tier.id}`;

        if (!state.unlocked) {
          try { sessionStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
          return;
        }

        let alreadyAttempted = false;
        try { alreadyAttempted = sessionStorage.getItem(storageKey) === '1'; } catch (e) { /* ignore */ }
        if (alreadyAttempted) return;

        window.__freeGiftAutoAdding = window.__freeGiftAutoAdding || {};
        if (window.__freeGiftAutoAdding[tier.id]) return;
        window.__freeGiftAutoAdding[tier.id] = true;

        fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1, properties: { [GIFT_PROPERTY_KEY]: tier.id } }],
          }),
        })
          .then((res) => {
            if (!res.ok) return res.json().then((err) => Promise.reject(err));
            return res.json();
          })
          .then(() => {
            try { sessionStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ }
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            this.onCartChange();
          })
          .catch((err) => console.error('cart-free-gift: failed to auto-add gift', err))
          .finally(() => {
            window.__freeGiftAutoAdding[tier.id] = false;
          });
      });
    }

    renderTierSelector(tier) {
      const isSingle = tier.products.length === 1;
      const giftIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.8301 0.730652C11.4277 0.778202 11.9471 1.12747 12.2207 1.66034L12.2217 1.66132C12.4585 2.13022 12.4709 2.66615 12.2627 3.1369L12.1602 3.33417C11.8502 3.84521 11.3075 4.15573 10.707 4.16132H10.5732C10.5354 4.17643 10.495 4.18475 10.4551 4.18475H8.29688V6.43182H13.5518C13.6573 6.4318 13.7441 6.34495 13.7441 6.23944V4.37714C13.7441 4.2717 13.6572 4.18477 13.5518 4.18475H12.6377C12.4495 4.18458 12.2959 4.03121 12.2959 3.84296C12.2959 3.6547 12.4495 3.50134 12.6377 3.50116H13.5518C14.0302 3.50555 14.4198 3.89767 14.4199 4.37714V6.23944C14.4199 6.72158 14.026 7.11623 13.5439 7.11639H12.9717V14.3986C12.9716 14.8808 12.5779 15.2746 12.0957 15.2746H3.7998C3.31781 15.2744 2.92389 14.8807 2.92383 14.3986V8.00018C2.92383 7.81192 3.07739 7.65757 3.26562 7.65741C3.45399 7.65741 3.6084 7.81182 3.6084 8.00018V14.3986C3.60846 14.5039 3.69452 14.5908 3.7998 14.591H12.0957C12.2012 14.591 12.288 14.5041 12.2881 14.3986V7.11639H2.35254C1.87031 7.11639 1.47559 6.72167 1.47559 6.23944V4.37714C1.47566 3.89497 1.87035 3.50116 2.35254 3.50116H3.86133C3.81907 3.44805 3.77897 3.39195 3.74219 3.33221L3.74121 3.33124C3.43133 2.82009 3.40899 2.19364 3.68262 1.66034C3.95622 1.12729 4.47645 0.778135 5.07422 0.730652C5.67134 0.683281 6.24183 0.939807 6.59668 1.42303L7.95215 3.26385L9.30762 1.42303C9.66252 0.939888 10.233 0.683222 10.8301 0.730652ZM2.35742 4.18475C2.25202 4.18487 2.16508 4.27172 2.16504 4.37714V6.23944C2.16507 6.34487 2.25202 6.43171 2.35742 6.43182H7.60938V4.18475H2.35742ZM5.17676 1.41034C5.16172 1.41099 5.14454 1.41229 5.12793 1.41229C4.77166 1.44413 4.46038 1.65241 4.29688 1.97186L4.27441 2.01678C4.13602 2.32782 4.15421 2.68169 4.33203 2.97577C4.51901 3.2849 4.84493 3.47133 5.20605 3.47382L7.27246 3.49335L6.04785 1.83026C5.85079 1.56347 5.54725 1.40936 5.21875 1.40936C5.20603 1.40937 5.19237 1.40966 5.17676 1.41034ZM10.6904 1.40936C10.3645 1.40936 10.0584 1.56352 9.86133 1.83026L9.83789 1.86151L9.83496 1.86053L8.63184 3.49335L10.7002 3.47675L10.834 3.46698C11.1412 3.42412 11.4108 3.24872 11.5742 2.9787C11.7613 2.66943 11.7767 2.29313 11.6123 1.97186C11.4479 1.65064 11.1341 1.44135 10.7754 1.41132H10.7744C10.7491 1.40901 10.7205 1.40936 10.6904 1.40936Z" fill="currentColor" stroke="currentColor" stroke-width="0.15"/></svg>`;

      if (isSingle) {
        const product = tier.products[0];
        const singleVariant = product.variants.length === 1;
        const firstVariant = product.variants[0];
        const price = firstVariant ? firstVariant.price : 0;

        // Always render the size select. When there's only one variant
        // (whether Shopify calls it "Default Title" or a real name like
        // "Chocolate / One size"), skip the placeholder option and mark that
        // single option selected, so the shopper sees a pre-filled dropdown
        // instead of an empty "Select size" prompt.
        const selectMarkup = `<select class="cart-free-gift__variant-select" data-gift-variant-select data-product-id="${product.id}">
          ${!singleVariant ? `<option value="">${this.config.strings.selectSize}</option>` : ''}
          ${product.variants
            .map(
              (v) =>
                `<option value="${v.id}" ${singleVariant ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${v.title}${!v.available ? ' - Sold out' : ''}</option>`
            )
            .join('')}
        </select>`;

        const buttonAttrs = singleVariant
          ? `data-variant-id="${firstVariant.available ? firstVariant.id : ''}" ${firstVariant.available ? '' : 'disabled'}`
          : 'disabled';

        return `<div class="cart-free-gift__tier" data-tier-id="${tier.id}" data-tier-mode="single">
          <p class="cart-free-gift__unlocked-label">${this.config.strings.giftUnlocked} ${giftIcon}</p>
          <div class="cart-free-gift__product-wrap">
            <div class="cart-free-gift__product">
              ${product.image ? `<img src="${product.image}" alt="${product.title}" width="64" height="64" loading="lazy">` : ''}
              <div class="cart-free-gift__product-info">
                <div class="wrapcart-flex">
                  <p class="cart-free-gift__product-title">${product.title}</p>
                  <div class="cart-free-gift__product-price cart__price">
                    <ins>${this.formatMoney(0)}</ins>
                    <del>${this.formatMoney(price)}</del>
                  </div>
                </div>
                <div class="wrapcart-flex">
                  ${selectMarkup}
                  <button type="button" class="btn btn--primary btn--solid cart-free-gift__add-btn" data-gift-add-btn data-tier-id="${tier.id}" ${buttonAttrs}>
                    ${this.config.strings.addForFree}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>`;
      }

      // Multiple gift options mode
      const requiredCount = tier.giftsRequiredToSelect || 1;
      const selectionMap = this.selections[tier.id] || {};
      const selectedProductIds = Object.keys(selectionMap);
      const selectedCount = selectedProductIds.length;

      // Button is enabled only when required number of products are selected AND all selected products have a size variant chosen
      const isComplete = selectedCount >= requiredCount && selectedProductIds.every((id) => Boolean(selectionMap[id]));

      const counterLabel = this.config.strings.giftsSelected
        .replace('__SELECTED__', selectedCount)
        .replace('__REQUIRED__', requiredCount);

      const isOpen = this.openAccordions[tier.id];

      if (!isOpen) {
        return `<div class="cart-free-gift__tier" data-tier-id="${tier.id}" data-tier-mode="multiple" data-required-count="${requiredCount}">
          <div class="cart-free-gift__unlocked-wrapper">
            <p class="cart-free-gift__unlocked-label">${this.config.strings.giftUnlocked} ${giftIcon}</p>
            <button type="button" class="btn btn--primary btn--solid btn--full cart-free-gift__toggle-btn" data-gift-toggle-btn data-tier-id="${tier.id}">
              Choose your gift
            </button>
          </div>
        </div>`;
      }

      return `<div class="cart-free-gift__tier" data-tier-id="${tier.id}" data-tier-mode="multiple" data-required-count="${requiredCount}">
        <div class="cart-free-gift__tier-header" data-gift-header data-tier-id="${tier.id}">
          <p class="cart-free-gift__unlocked-label">${this.config.strings.giftUnlocked} ${giftIcon}</p>
          <p class="cart-free-gift__counter" data-gift-counter>${counterLabel}</p>
        </div>
        <div class="cart-free-gift__product-list">
          ${this._productListMarkup(tier, selectionMap)}
        </div>
        <button type="button" class="btn btn--primary btn--solid btn--full cart-free-gift__add-btn" data-gift-add-btn data-tier-id="${tier.id}" ${isComplete ? '' : 'disabled'}>
          ${this.config.strings.addToCart}
        </button>
      </div>`;
    }

    _productListMarkup(tier, selectionMap) {
      return tier.products
        .map((product) => {
          const isSelected = Boolean(selectionMap && product.id in selectionMap);
          const selectedVariantId = selectionMap ? selectionMap[product.id] : '';

          const singleVariant = product.variants.length === 1;
          const firstVariant = product.variants[0];
          const price = firstVariant ? firstVariant.price : 0;

          // Always render the size select. Single-variant products get their
          // one option pre-selected instead of showing an empty prompt.
          const selectMarkup = `<div class="wrapcart-flex">
              <select class="cart-free-gift__variant-select" data-gift-variant-select data-tier-id="${tier.id}" data-product-id="${product.id}">
                ${!singleVariant ? `<option value="">${this.config.strings.selectSize}</option>` : ''}
                ${product.variants
                  .map(
                    (v) =>
                      `<option value="${v.id}" ${singleVariant || String(selectedVariantId) === String(v.id) ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${v.title}${!v.available ? ' - Sold out' : ''}</option>`
                  )
                  .join('')}
              </select>
            </div>`;

          return `<div class="cart-free-gift__product${isSelected ? ' is-selected' : ''}" data-gift-product="${product.id}">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" width="60" height="60" loading="lazy">` : ''}
            <div class="cart-free-gift__product-info">
              <div class="wrapcart-flex">
                <p class="cart-free-gift__product-title">${product.title}</p>
                <div class="cart-free-gift__product-price cart__price">
                  <ins>${this.formatMoney(0)}</ins>
                  <del>${this.formatMoney(price)}</del>
                </div>
              </div>
              ${selectMarkup}
            </div>
          </div>`;
        })
        .join('');
    }

    bindSelectorEvents() {
      this.selector.querySelectorAll('[data-gift-variant-select]').forEach((select) => {
        select.addEventListener('change', (e) => this.onVariantSelect(e));
      });

      this.selector.querySelectorAll('[data-gift-add-btn]').forEach((button) => {
        button.addEventListener('click', (e) => this.onAddGiftClick(e));
      });

      this.selector.querySelectorAll('[data-gift-toggle-btn]').forEach((button) => {
        button.addEventListener('click', (e) => {
          const tierId = e.currentTarget.dataset.tierId;
          this.openAccordions[tierId] = true;
          this.render();
        });
      });

      this.selector.querySelectorAll('[data-gift-header]').forEach((header) => {
        header.addEventListener('click', (e) => {
          const tierId = e.currentTarget.dataset.tierId;
          this.openAccordions[tierId] = false;
          this.render();
        });
      });

      this.selector.querySelectorAll('[data-gift-product]').forEach((productEl) => {
        productEl.addEventListener('click', (e) => {
          if (e.target.closest('[data-gift-variant-select]')) return;

          const productId = e.currentTarget.dataset.giftProduct;
          const tierEl = e.currentTarget.closest('.cart-free-gift__tier');
          const tierId = tierEl.dataset.tierId;
          const select = e.currentTarget.querySelector('[data-gift-variant-select]');

          if (!this.selections[tierId]) this.selections[tierId] = {};

          const isSelected = Boolean(productId in this.selections[tierId]);

          if (isSelected) {
            delete this.selections[tierId][productId];
            if (select) {
              // Only reset to the empty placeholder if one exists - single-
              // variant selects have no blank option, so leave them as-is.
              const hasPlaceholder = Array.from(select.options).some((o) => o.value === '');
              if (hasPlaceholder) select.value = '';
            }
          } else {
            const selectedCount = Object.keys(this.selections[tierId]).length;
            const requiredCount = parseInt(tierEl.dataset.requiredCount, 10) || 1;
            if (selectedCount >= requiredCount) {
              if (requiredCount === 1) {
                this.selections[tierId] = {};
              } else {
                alert(`You can only select up to ${requiredCount} free gift(s).`);
                return;
              }
            }

            const variantId = select ? select.value : '';
            if (!variantId) return; // sold out or nothing selected

            this.selections[tierId][productId] = variantId;
          }
          this.render();
        });
      });
    }

    onVariantSelect(event) {
      const select = event.currentTarget;
      const tierEl = select.closest('.cart-free-gift__tier');
      const tierId = tierEl.dataset.tierId;
      const mode = tierEl.dataset.tierMode;
      const productId = select.dataset.productId;
      const variantId = select.value;

      if (mode === 'single') {
        const addBtn = tierEl.querySelector('[data-gift-add-btn]');
        addBtn.disabled = !variantId;
        addBtn.dataset.variantId = variantId;
        return;
      }

      // multiple mode
      if (!this.selections[tierId]) this.selections[tierId] = {};

      const isPreviouslySelected = Boolean(productId in this.selections[tierId]);

      if (!isPreviouslySelected) {
        const selectedCount = Object.keys(this.selections[tierId]).length;
        const requiredCount = parseInt(tierEl.dataset.requiredCount, 10) || 1;
        if (selectedCount >= requiredCount) {
          if (requiredCount === 1) {
            this.selections[tierId] = {};
          } else {
            alert(`You can only select up to ${requiredCount} free gift(s).`);
            select.value = '';
            return;
          }
        }
      }

      this.selections[tierId][productId] = variantId;

      this.render();
    }

    onAddGiftClick(event) {
      const button = event.currentTarget;
      const tierId = button.dataset.tierId;
      const tierEl = button.closest('.cart-free-gift__tier');
      const mode = tierEl.dataset.tierMode;

      let itemsToAdd = [];

      if (mode === 'single') {
        const variantId = button.dataset.variantId;
        if (!variantId) return;
        itemsToAdd = [{ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY_KEY]: tierId } }];
      } else {
        const selection = this.selections[tierId] || {};
        const validItems = Object.entries(selection).filter(([pid, vid]) => Boolean(vid));
        const requiredCount = parseInt(tierEl.dataset.requiredCount, 10) || 1;

        if (validItems.length < requiredCount) return;

        itemsToAdd = validItems.map(([pid, variantId]) => ({
          id: variantId,
          quantity: 1,
          properties: { [GIFT_PROPERTY_KEY]: tierId },
        }));
      }

      button.disabled = true;
      button.classList.add('is-loading');

      fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(err));
          return res.json();
        })
        .then(() => {
          if (mode === 'multiple') this.selections[tierId] = {};
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.dispatchEvent(
            new CustomEvent('theme:cart-drawer:open', {
              bubbles: true,
              detail: { target: document.querySelector('cart-drawer') },
            })
          );
          this.onCartChange();
        })
        .catch((err) => {
          console.error('cart-free-gift: failed to add gift to cart', err);
          button.disabled = false;
          button.classList.remove('is-loading');
        });
    }

    /**
     * Remove all gift line items for a given tier.
     * Called by "Change gift" button via event delegation, or can be called externally.
     */
    removeGiftForTier(tierId) {
      const lines = this.items.filter(
        (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === tierId
      );
      if (!lines.length) return;

      const updates = {};
      lines.forEach((line) => {
        updates[line.key] = 0;
      });

      fetch(`${window.theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'post',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      })
        .then(() => {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          this.onCartChange();
        })
        .catch((err) => console.error('cart-free-gift: failed to remove gift', err));
    }
  }

  customElements.define('cart-free-gift', CartFreeGift);
})();